import SpaceBetween from "@amzn/awsui-components-react/polaris/space-between";
import {
  Alert,
  Box,
  Button,
  ColumnLayout,
  ExpandableSection,
  FormField,
  Input,
  Link,
  Modal,
  Spinner,
} from "@amzn/awsui-components-react";
import ErrorAlert from "../common/ErrorAlert";
import type {
  DLQRedriveInfoResult,
  Stage,
} from "../../lusca-search-api/generated-src";
import { generateMcmUrl } from "../../helpers/logUtils";
import { extractRedriveErrorMessage } from "./dlqRedriveError";
import { DlqRedriveExecutionDetails } from "./DlqRedriveExecutionDetails";
import { DlqRedriveDetailsTable } from "./table/DlqRedriveDetailsTable";

interface Props {
  combinedError: string | null;
  details: DLQRedriveInfoResult | null;
  loadingDetails: boolean;
  onBack: () => void;
  onRetry: () => void;
  onRetryConfirmTextChange: (value: string) => void;
  onRetryDismiss: () => void;
  onRetryOpen: () => void;
  retryConfirmText: string;
  retrying: boolean;
  selectedExecutionArn: string;
  showRetryConfirm: boolean;
  stage: Stage;
}

export function DlqRedriveExecutionView({
  combinedError,
  details,
  loadingDetails,
  onBack,
  onRetry,
  onRetryConfirmTextChange,
  onRetryDismiss,
  onRetryOpen,
  retryConfirmText,
  retrying,
  selectedExecutionArn,
  showRetryConfirm,
  stage,
}: Props) {
  const isDeleteRetry =
    !!details?.metadata?.deleteWithoutRedriving && !details?.metadata?.dryRun;
  const confirmDisabled =
    retrying || (isDeleteRetry && retryConfirmText.trim() !== "confirm");

  return (
    <SpaceBetween size="xl">
      <div>
        <SpaceBetween size="l">
          {combinedError && (
            <ErrorAlert content={combinedError} header="DLQ Redrive Error" />
          )}
          <Button iconName="arrow-left" onClick={onBack} variant="link">
            Back to all redrives
          </Button>
          <DlqRedriveExecutionDetails
            status={details?.status}
            metadata={details?.metadata}
            executionArn={selectedExecutionArn}
            stage={stage}
            loading={!details && loadingDetails}
          />
          {details?.status === "PENDING" && (
            <SpaceBetween size="xs" direction="horizontal" alignItems="center">
              <Spinner
                size="normal"
                data-testid="redrive-auto-refresh-spinner"
              />
              <Box variant="small" color="text-status-inactive">
                The redrive is running. This page updates automatically.
              </Box>
            </SpaceBetween>
          )}
          {details?.status === "ERROR" &&
            (() => {
              // The cause holds the readable message; result.error is just
              // the exception class. Fall back so we never hide an error.
              const causeMessage = extractRedriveErrorMessage(
                details.result?.cause
              );
              const friendly =
                causeMessage ||
                details.result?.error ||
                details.error ||
                "The redrive execution failed.";
              // Only show the raw cause when it adds detail.
              const rawDetail =
                details.result?.cause &&
                details.result.cause.trim() !== friendly.trim()
                  ? details.result.cause
                  : null;
              const counts = details.result?.counts;

              return (
                <Alert type="error" header="DLQ redrive failed">
                  <SpaceBetween size="s">
                    <Box>{friendly}</Box>
                    {counts && counts.total ? (
                      <Box>
                        Of {counts.total} message(s): {counts.succeeded ?? 0}{" "}
                        processed, {counts.failed ?? 0} failed,{" "}
                        {counts.pending ?? 0} never ran. Any processed results
                        are shown below; the rest can be re-run via Retry.
                      </Box>
                    ) : null}
                    {rawDetail && (
                      <ExpandableSection headerText="Raw error details">
                        <Box
                          variant="code"
                          fontSize="body-s"
                          color="text-status-error"
                        >
                          {rawDetail}
                        </Box>
                      </ExpandableSection>
                    )}
                    <Button
                      onClick={onRetryOpen}
                      iconName="refresh"
                      data-testid="retry-redrive"
                    >
                      Retry with same parameters
                    </Button>
                  </SpaceBetween>
                </Alert>
              );
            })()}
          {details && details.result && (
            <DlqRedriveDetailsTable
              details={details}
              loading={loadingDetails}
              stage={stage}
            />
          )}
        </SpaceBetween>
        {showRetryConfirm && (
          <Modal
            visible={showRetryConfirm}
            onDismiss={onRetryDismiss}
            header={isDeleteRetry ? "Delete from DLQ" : "Retry DLQ redrive"}
            footer={
              <Box float="right">
                <SpaceBetween direction="horizontal" size="xs">
                  <Button
                    variant="link"
                    onClick={onRetryDismiss}
                    disabled={retrying}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="primary"
                    onClick={onRetry}
                    loading={retrying}
                    disabled={confirmDisabled}
                    data-testid="confirm-retry-redrive"
                  >
                    {isDeleteRetry ? "Delete from DLQ" : "Retry"}
                  </Button>
                </SpaceBetween>
              </Box>
            }
          >
            <SpaceBetween size="m">
              <Box>
                This starts a new execution with the same parameters as the
                failed one. It will be attributed to you.
              </Box>
              {isDeleteRetry && (
                <Alert type="warning">
                  This is a <b>delete without redrive</b> — it permanently
                  deletes the DLQ items. Ensure the MCM is still approved and In
                  Progress before continuing.
                </Alert>
              )}
              <ColumnLayout columns={2} variant="text-grid">
                <div>
                  <Box variant="awsui-key-label">Type</Box>
                  <div>{details?.metadata?.requestType ?? "unknown"}</div>
                </div>
                <div>
                  <Box variant="awsui-key-label">Source</Box>
                  <div>
                    {details?.metadata?.messageIdsS3Uri ??
                      (details?.metadata?.messageCount != null
                        ? `${details.metadata.messageCount} message id(s)`
                        : "-")}
                  </div>
                </div>
                {isDeleteRetry && (
                  <div>
                    <Box variant="awsui-key-label">MCM</Box>
                    <div>
                      {details?.metadata?.mcmId ? (
                        <Link
                          href={generateMcmUrl(stage, details.metadata.mcmId)}
                          external
                          externalIconAriaLabel="Opens in a new tab"
                        >
                          {details.metadata.mcmId}
                        </Link>
                      ) : (
                        "-"
                      )}
                    </div>
                  </div>
                )}
              </ColumnLayout>
              {isDeleteRetry && (
                <FormField label="To confirm this deletion, type “confirm”.">
                  <Input
                    value={retryConfirmText}
                    onChange={({ detail }) =>
                      onRetryConfirmTextChange(detail.value)
                    }
                    placeholder="confirm"
                  />
                </FormField>
              )}
            </SpaceBetween>
          </Modal>
        )}
      </div>
    </SpaceBetween>
  );
}
